import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

import {
  buildTaskPayload,
  createTasks,
  validateTask,
} from "../../services/taskService";
import AssignMemberSelect, {
  canAssignToOthers,
  resolveAssigneeId,
} from "../AssignMemberSelect/AssignMemberSelect";
import "./QuickAddTable.scss";

const QuickAddTable = forwardRef(function QuickAddTable(
  { context, onCreated },
  ref,
) {
  const [rows, setRows] = useState([
    { id: Date.now(), title: "", remark: "", assigneeId: "", status: "idle" },
  ]);
  const tableRef = useRef(null);

  // Only Creator/Approver get the "Assign to" column.
  // (These create backend "tasks", i.e. stages, so the "task" rules apply.)
  const showAssign = canAssignToOthers(context?.currentUserRole, "task");

  // Base payload from the service, then apply the assignee. Nothing chosen (or
  // a role that can't assign) leaves the service's default: the logged-in user.
  const buildPayloadFor = (row) => {
    const payload = buildTaskPayload(row, context);
    const assigneeId = resolveAssigneeId({
      selectedEmpId: row.assigneeId,
      currentEmpId: context?.currentEmpId,
      members: context?.members,
      currentUserRole: context?.currentUserRole,
      scope: "task",
    });
    const isSomeoneElse =
      assigneeId != null &&
      String(assigneeId) !== String(context?.currentEmpId);
    if (!isSomeoneElse) return payload;

    const member = (context?.members || []).find(
      (m) => String(m.empId) === String(assigneeId),
    );
    return {
      ...payload,
      assignedEmpId: assigneeId,
      // accessId = the assignee's project access record
      accessId: member?.accessId ?? payload.accessId ?? null,
    };
  };

  const addBlankRow = () =>
    setRows((r) => [
      ...r,
      {
        id: Date.now() + Math.random(),
        title: "",
        remark: "",
        assigneeId: "",
        status: "idle",
      },
    ]);

  const handleChange = (index, field, value) => {
    setRows((r) => {
      const copy = [...r];
      copy[index][field] = value;
      return copy;
    });
  };

  const focusLastTitle = () => {
    setTimeout(() => {
      const inputs = tableRef.current?.querySelectorAll("input[name='title']");
      if (inputs?.length) inputs[inputs.length - 1].focus();
    }, 30);
  };

  // Flush a single row to server (no parent close / no onCreated call here)
  const flushSingleRow = async (rowIndex) => {
    const copy = [...rows];
    const row = copy[rowIndex];
    try {
      validateTask(row);
      copy[rowIndex].status = "saving";
      setRows(copy);

      const payload = [buildPayloadFor(row)];
      const res = await createTasks(payload);

      // mark success if API returned data array
      if (res?.data && res.data.length) {
        copy[rowIndex].status = "done";
      } else {
        copy[rowIndex].status = "done";
      }
      setRows(copy);
    } catch (err) {
      copy[rowIndex].status = "error";
      setRows(copy);
      console.error("QuickAdd error:", err);
    }
  };
  // focus the first Title input when the component mounts (modal opened)
  const focusFirstTitle = () => {
    // small timeout helps when modal animation / mount timing interferes
    setTimeout(() => {
      const inputs = tableRef.current?.querySelectorAll("input[name='title']");
      if (inputs?.length) inputs[0].focus();
    }, 30);
  };

  useEffect(() => {
    focusFirstTitle();
  }, []); // run once on mount

  // Expose flushAll via ref so parent modal can call Save All & Close
  useImperativeHandle(ref, () => ({
    flushAll: async () => {
      const toFlush = rows
        .map((r, i) => ({ row: r, index: i }))
        .filter(({ row }) => row.title?.trim() && row.status !== "done");

      if (!toFlush.length) return { created: [] };

      // set all to saving
      setRows((prev) =>
        prev.map((r) =>
          r.title?.trim() && r.status !== "done"
            ? { ...r, status: "saving" }
            : r,
        ),
      );

      // build payload array preserving order
      const payloadArray = toFlush.map(({ row }) => buildPayloadFor(row));

      try {
        const res = await createTasks(payloadArray);
        // update statuses: assume server returns data[] in same order
        setRows((prev) => {
          const copy = [...prev];
          let j = 0;
          for (let i = 0; i < copy.length; i++) {
            if (copy[i].title?.trim() && copy[i].status === "saving") {
              copy[i].status = res?.data?.[j] ? "done" : "done";
              j++;
            }
          }
          return copy;
        });

        if (typeof onCreated === "function") onCreated(res.data || []);
        // append a fresh blank row and focus it
        addBlankRow();
        focusLastTitle();
        return { created: res.data || [] };
      } catch (err) {
        // mark errors for previously saving rows
        setRows((prev) =>
          prev.map((r) =>
            r.status === "saving" ? { ...r, status: "error" } : r,
          ),
        );
        console.error("Batch quick-add failed:", err);
        throw err;
      }
    },
  }));

  const handleKeyDown = async (e, index, field) => {
    if (e.key === "Tab" && field === "title") {
      e.preventDefault();
      const remark = document.getElementById(`remark-${rows[index].id}`);
      remark?.focus();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      // if currently focused in title and title empty, ignore
      if (!rows[index].title?.trim()) {
        if (field === "title") return;
      }

      // Save current row (send to backend) — do NOT close modal
      await flushSingleRow(index);

      // Add next blank row
      addBlankRow();
      focusLastTitle();
    }
  };

  return (
    <div className="quick-add-table" ref={tableRef}>
      <table>
        <thead>
          <tr>
            <th style={{ width: showAssign ? "38%" : "55%" }}>Title</th>
            <th style={{ width: showAssign ? "26%" : "35%" }}>Remark</th>
            {showAssign && <th style={{ width: "24%" }}>Assign to</th>}
            <th style={{ width: showAssign ? "12%" : "10%" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id}>
              <td>
                <input
                  name="title"
                  value={row.title}
                  placeholder="Task title — press Tab then Enter"
                  onChange={(e) => handleChange(i, "title", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, i, "title")}
                />
              </td>
              <td>
                <input
                  id={`remark-${row.id}`}
                  value={row.remark}
                  placeholder="Optional remark"
                  onChange={(e) => handleChange(i, "remark", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, i, "remark")}
                />
              </td>
              {showAssign && (
                <td>
                  <AssignMemberSelect
                    id={`assignee-${row.id}`}
                    label="Assign to"
                    showLabel={false}
                    members={context?.members}
                    value={row.assigneeId}
                    onChange={(v) => handleChange(i, "assigneeId", v)}
                    currentEmpId={context?.currentEmpId}
                    currentUserRole={context?.currentUserRole}
                    scope="task"
                    disabled={row.status === "saving" || row.status === "done"}
                  />
                </td>
              )}
              <td className={`status ${row.status}`}>
                {row.status === "idle" && ""}
                {row.status === "saving" && "Saving..."}
                {row.status === "done" && "✓"}
                {row.status === "error" && "✕"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="hint">Tab to Remark → Enter to save & open next</div>
    </div>
  );
});

export default QuickAddTable;
